import DataMap from 'assets/components/dataMap/DataMap';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { roundToStep } from 'assets/utils/data/Number';
import { isEmpty, values } from 'lodash';
import RefundType from 'models/enums/RefundType';
import { ContractRefundSummariesTable } from '../../../components/ContractRefundSummariesTable';
import { RefundDistributionTable, RefundSubfeeTable } from '../../../components/RefundDistributionTable';
import {
  ContractRefundForm,
  useContractRefundProvider,
  useContractRefundRecalculation,
} from '../../../Sales.ContractRefund.Hooks';

const SalesContractRefundProcessDistributions = () => {
  const { lang } = getLocales();
  const { form } = useModalForm<ContractRefundForm>();
  const { originalTotals, selected, isRecalculating } = useContractRefundProvider();

  const data = form.data as Api.Sales.ContractRefund.RefundPricingInfo.RefundPriceInfo;
  const prorateRemainingTimeOrKmAtRisk = selected?.refundConfigTemplateJson?.prorateRemainingTimeOrKmAtRisk;
  const riskFactor = selected?.riskFactor ?? 1;
  function getRefundType(key: string) {
    return (
      selected?.refundConfigTemplateJson.masterTemplatesRefundType[key] ??
      selected?.refundConfigTemplateJson.insurersRefundType[key]
    );
  }

  useContractRefundRecalculation(form);

  return (
    <FormWrapper isLoading={isRecalculating}>
      <ContractRefundSummariesTable
        data={{ ...data, excludeAdministrationFee: form.data?.excludeAdministrationFee }}
        actions={{
          dealerProfit: (
            <CheckWrapper style={{ display: 'flex', gap: Theme.Size.XXS }}>
              <CheckInput
                name="excludeDealerProfit"
                label={lang.exclude}
                class={inputStyles.check.switch}
                value={form.data.excludeDealerProfit}
                onChange={form.update}
                htmlElementProps={{ title: form.data.excludeDealerProfit ? lang.disable : lang.enable }}
              />
            </CheckWrapper>
          ),
        }}
        onAdminFeeExclusion={({ excludeAdministrationFee }) => {
          form.update({ excludeAdministrationFee });
        }}
      />
      &nbsp;
      <h1>{lang.distributions}</h1>
      {!!values(form.data?.pricingDistributions).length && (
        <RefundDistributionTable
          title={lang.pricingDistributions}
          originalRetainedTotal={originalTotals.retainedPricingDistributions}
          distributions={form.data?.pricingDistributions as Utils.ContractRefundDistribution}
          onRetainedValueChange={({ key, amount: formAmount }) => {
            const distributions = data?.pricingDistributions[key] ?? {};
            const amount = formAmount > distributions.original.amount ? distributions.original?.amount : formAmount;
            form.update({
              pricingDistributions: {
                ...data?.pricingDistributions,
                [key]: {
                  ...distributions,
                  refunded: {
                    ...distributions.refunded,
                    amount: roundToStep(
                      ((distributions.original?.amount ?? 0) - (amount ?? 0)) *
                        (getRefundType(key) !== RefundType.PRO_RATED || !prorateRemainingTimeOrKmAtRisk
                          ? 1
                          : riskFactor),
                      0.01
                    ),
                  },
                  retained: {
                    ...distributions.retained,
                    amount,
                  },
                },
              },
            });
          }}
        />
      )}
      {!!values(form.data?.distributorDistributions).length && (
        <RefundDistributionTable
          title={lang.distributorDistributions}
          originalRetainedTotal={originalTotals.retainedDistributorDistributions}
          distributions={form.data?.distributorDistributions as Utils.ContractRefundDistribution}
          onRetainedValueChange={({ key, amount: formAmount }) => {
            const distributions = data?.distributorDistributions[key] ?? {};
            const amount = formAmount > distributions.original.amount ? distributions.original?.amount : formAmount;
            form.update({
              distributorDistributions: {
                ...data?.distributorDistributions,
                [key]: {
                  ...distributions,
                  refunded: {
                    ...distributions.refunded,
                    amount: roundToStep(
                      ((distributions.original?.amount ?? 0) - (amount ?? 0)) *
                        (getRefundType(key) !== RefundType.PRO_RATED || !prorateRemainingTimeOrKmAtRisk
                          ? 1
                          : riskFactor),
                      0.01
                    ),
                  },
                  retained: {
                    ...distributions.retained,
                    amount,
                  },
                },
              },
            });
          }}
        />
      )}
      &nbsp;
      {form.data?.subfees && !isEmpty(form.data?.subfees) && (
        <>
          <h1>{lang.surcharges}</h1>
          <RefundSubfeeTable
            subfees={form.data?.subfees as Utils.ContractRefundSubfee[]}
            originalRetainedTotal={originalTotals.retainedSubfees}
            onRetainedValueChange={({ id, amount: formAmount }) => {
              const foundSubfee = data.subfees?.find((it) => it.subfeeId === id);
              const amount = formAmount > foundSubfee.original.amount ? foundSubfee.original?.amount : formAmount;
              if (foundSubfee?.retained) {
                foundSubfee.retained.amount = amount;
                foundSubfee.refunded.amount = roundToStep(
                  ((foundSubfee.original?.amount ?? 0) - (amount ?? 0)) *
                    (selected?.refundConfigTemplateJson.surchargesRefundType[foundSubfee.name] !==
                      RefundType.PRO_RATED || !prorateRemainingTimeOrKmAtRisk
                      ? 1
                      : riskFactor),
                  0.01
                );
              }
              form.update({
                subfees: [...(data.subfees ?? [])],
              });
            }}
          />
        </>
      )}
      {form.data?.enhancements && !isEmpty(form.data?.enhancements) && (
        <>
          <h1>{lang.enhancements}</h1>

          <DataMap
            data={form.data?.enhancements}
            render={({ data: enhancement }) => {
              //This is just in case DataMap ever clones "data" property
              const foundEnhancement = form.data?.enhancements?.find(
                (it) => it.enhancementId === enhancement.enhancementId
              );
              return (
                <div key={enhancement?.enhancementId}>
                  <h2>{enhancement.name || lang.unknown}:</h2>

                  {!!values(enhancement?.pricingDistributions).length && (
                    <RefundDistributionTable
                      title={lang.pricingDistributions}
                      originalRetainedTotal={
                        originalTotals.retainedEnhancements.find((it) => it.id === enhancement?.enhancementId)
                          ?.retainedPricingDistributions
                      }
                      distributions={enhancement?.pricingDistributions as Utils.ContractRefundDistribution}
                      onRetainedValueChange={
                        !foundEnhancement
                          ? undefined
                          : ({ key, amount: formAmount }) => {
                              const distributions = foundEnhancement?.pricingDistributions[key] ?? {};
                              const amount =
                                formAmount > distributions.original.amount
                                  ? distributions.original?.amount
                                  : formAmount;
                              distributions.retained.amount = amount;
                              distributions.refunded.amount = roundToStep(
                                ((distributions.original?.amount ?? 0) - (amount ?? 0)) *
                                  (getRefundType(key) !== RefundType.PRO_RATED || !prorateRemainingTimeOrKmAtRisk
                                    ? 1
                                    : riskFactor),
                                0.01
                              );

                              form.update({
                                enhancements: [...(form.data?.enhancements ?? [])],
                              });
                            }
                      }
                    />
                  )}
                  {!!values(enhancement?.distributorDistributions).length && (
                    <RefundDistributionTable
                      title={lang.distributorDistributions}
                      originalRetainedTotal={
                        originalTotals.retainedEnhancements.find((it) => it.id === enhancement?.enhancementId)
                          ?.retainedDistributorDistributions
                      }
                      distributions={enhancement?.distributorDistributions as Utils.ContractRefundDistribution}
                      onRetainedValueChange={
                        !foundEnhancement
                          ? undefined
                          : ({ key, amount: formAmount }) => {
                              const distributions = foundEnhancement?.distributorDistributions[key] ?? {};
                              const amount =
                                formAmount > distributions.original.amount
                                  ? distributions.original?.amount
                                  : formAmount;
                              distributions.retained.amount = amount;
                              distributions.refunded.amount = roundToStep(
                                ((distributions.original?.amount ?? 0) - (amount ?? 0)) *
                                  (getRefundType(key) !== RefundType.PRO_RATED || !prorateRemainingTimeOrKmAtRisk
                                    ? 1
                                    : riskFactor),
                                0.01
                              );

                              form.update({
                                enhancements: [...(form.data?.enhancements ?? [])],
                              });
                            }
                      }
                    />
                  )}
                </div>
              );
            }}
          />
        </>
      )}
    </FormWrapper>
  );
};

export default SalesContractRefundProcessDistributions;
